import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { RealTimeTranscriptLine, TranscriptHighlight } from '../types';
import type { TextSegment } from '../types';
import { formatTimestamp, formatUtcDateTime, formatNoteTimestamp } from '../utils/formatters';
import buildTextSegments from '../utils/buildTextSegments';
import { renderTextWithDocRefs } from '../utils/docRefLinks';

const getHighlightLabel = (seg: TextSegment): string => {
  if (seg.isRemote) {
    if (seg.hasTags) return 'View tag';
    if (seg.hasNote) return 'View note';
    return 'View highlight';
  }
  if (seg.hasTags) return 'Open tag';
  if (seg.hasNote) return 'Open note';
  return 'Open highlight';
};

const getHighlightTooltip = (
  seg: TextSegment,
  highlights: TranscriptHighlight[],
): string => {
  const label = getHighlightLabel(seg);
  const hl = seg.highlightId ? highlights.find((h) => h.highlightId === seg.highlightId) : null;
  if (!hl?.createdAt) return label;
  const ts = formatNoteTimestamp(hl.createdAt);
  return `${label} · ${ts}`;
};

const getTeamTooltip = (
  seg: TextSegment,
  highlights: TranscriptHighlight[],
): string => {
  let label: string;
  if (seg.isRemote) label = 'Shared with the team';
  else if (seg.hasTags) label = 'This tag is shared with the Team';
  else if (seg.hasNote) label = 'This note is shared with the Team';
  else label = 'This highlight is shared with the Team';

  const hl = seg.highlightId ? highlights.find((h) => h.highlightId === seg.highlightId) : null;
  if (!hl?.createdAt) return label;
  const ts = formatNoteTimestamp(hl.createdAt);
  return `${label} · ${ts}`;
};

export const EMPTY_HIGHLIGHTS: TranscriptHighlight[] = [];
const DRAIN_TICK_MS = 20;
const CHARS_PER_TICK = 1;

type TranscriptLineProps = {
  line: RealTimeTranscriptLine;
  lineHighlights?: TranscriptHighlight[];
  navigatedHighlightId?: string | null;
  shouldAnimateLive?: boolean;
  searchHighlight?: 'match' | 'active' | null;
  showTimestamps?: boolean;
  onDocRefClick?: (refText: string) => void;
};

const TranscriptLine = React.memo(
  ({
    line,
    lineHighlights = EMPTY_HIGHLIGHTS,
    navigatedHighlightId = null,
    shouldAnimateLive = true,
    searchHighlight = null,
    showTimestamps = true,
    onDocRefClick,
  }: TranscriptLineProps) => {
    const initialRenderedText = line.type ? '' : (line.text ?? '');
    const [animatedLineText, setAnimatedLineText] = useState(initialRenderedText);
    const chunkBufferRef = useRef('');
    const displayedTextRef = useRef(initialRenderedText);
    const drainTimerRef = useRef<number | null>(null);
    const lastDrainTimeRef = useRef<number>(0);

    const stopDrainLoop = useCallback(() => {
      if (drainTimerRef.current != null) {
        window.cancelAnimationFrame(drainTimerRef.current);
        drainTimerRef.current = null;
      }
    }, []);

    const setDisplayedText = useCallback((nextText: string) => {
      displayedTextRef.current = nextText;
      setAnimatedLineText(nextText);
    }, []);

    const startDrainLoop = useCallback(() => {
      if (drainTimerRef.current != null) return;
      const drainLoop = (timestamp: number) => {
        if (timestamp - lastDrainTimeRef.current < DRAIN_TICK_MS) {
          drainTimerRef.current = window.requestAnimationFrame(drainLoop);
          return;
        }
        lastDrainTimeRef.current = timestamp;

        if (!chunkBufferRef.current) {
          stopDrainLoop();
          return;
        }

        const take = chunkBufferRef.current.slice(0, CHARS_PER_TICK);
        chunkBufferRef.current = chunkBufferRef.current.slice(CHARS_PER_TICK);
        setDisplayedText(`${displayedTextRef.current}${take}`);

        if (chunkBufferRef.current) {
          drainTimerRef.current = window.requestAnimationFrame(drainLoop);
        } else {
          stopDrainLoop();
        }
      };

      drainTimerRef.current = window.requestAnimationFrame(drainLoop);
    }, [setDisplayedText, stopDrainLoop]);

    const commonPrefixLength = useCallback((left: string, right: string) => {
      const max = Math.min(left.length, right.length);
      let i = 0;
      while (i < max && left[i] === right[i]) i += 1;
      return i;
    }, []);

    useEffect(() => {
      const nextText = line.text ?? '';
      const normalizedType = line.type?.toLowerCase();
      const isBootstrapLine = !normalizedType;
      const isLineFinalized = normalizedType === 'linefinalized';
      const isLineCorrected = normalizedType === 'linecorrected';
      const isReflowApplied = normalizedType === 'reflowapplied';
      const isMultiCorrected = normalizedType === 'multicorrected';

      // Bootstrap lines and bulk-correction events (reflow/multicorrect) are always
      // instant — no typewriter animation, cursor stays at end of current open line.
      if (isBootstrapLine || isReflowApplied || isMultiCorrected) {
        chunkBufferRef.current = '';
        setDisplayedText(nextText);
        stopDrainLoop();
        return;
      }

      const displayed = displayedTextRef.current;

      // LineFinalized always animates regardless of shouldAnimateLive — the line
      // just transitioned from open→closed so shouldAnimateLive is already false.
      if (isLineFinalized) {
        const prefixLen = commonPrefixLength(displayed, nextText);
        const stablePrefix = nextText.slice(0, prefixLen);
        const suffix = nextText.slice(prefixLen);
        chunkBufferRef.current = suffix;
        setDisplayedText(stablePrefix);
        if (suffix) startDrainLoop();
        else stopDrainLoop();
        return;
      }

      const canAnimate =
        shouldAnimateLive &&
        (typeof document === 'undefined' || document.visibilityState === 'visible');

      if (!canAnimate) {
        chunkBufferRef.current = '';
        setDisplayedText(nextText);
        stopDrainLoop();
        return;
      }

      // LineCorrected on the live open line: animate new characters one at a time.
      if (isLineCorrected) {
        const prefixLen = commonPrefixLength(displayed, nextText);
        const stablePrefix = nextText.slice(0, prefixLen);
        const suffix = nextText.slice(prefixLen);
        chunkBufferRef.current = suffix;
        setDisplayedText(stablePrefix);
        if (suffix) startDrainLoop();
        else stopDrainLoop();
        return;
      }

      chunkBufferRef.current = '';
      setDisplayedText(nextText);
      stopDrainLoop();
    }, [
      line.text,
      line.type,
      shouldAnimateLive,
      commonPrefixLength,
      setDisplayedText,
      startDrainLoop,
      stopDrainLoop,
    ]);

    useEffect(() => stopDrainLoop, [stopDrainLoop]);

    // When shouldAnimateLive flips false (another line became the active one),
    // snap the remaining buffer to text synchronously before the frame is painted.
    // useLayoutEffect fires before paint, unlike useEffect, which prevents a
    // one-frame window where two drain loops run simultaneously.
    const prevShouldAnimateLiveRef = useRef(shouldAnimateLive);
    useLayoutEffect(() => {
      const prev = prevShouldAnimateLiveRef.current;
      prevShouldAnimateLiveRef.current = shouldAnimateLive;
      if (prev && !shouldAnimateLive) {
        // LineFinalized: don't snap — the useEffect isLineFinalized path will
        // animate the remaining suffix. Snapping here would leave nothing to animate.
        if (line.type?.toLowerCase() === 'linefinalized') return;
        const remainder = chunkBufferRef.current;
        if (remainder) {
          chunkBufferRef.current = '';
          setDisplayedText(displayedTextRef.current + remainder);
        }
        stopDrainLoop();
      }
    }, [shouldAnimateLive, line.type, setDisplayedText, stopDrainLoop]);

    const displayText = animatedLineText;
    const timeLabel = line.finalizedAtUtc
      ? formatUtcDateTime(line.finalizedAtUtc)
      : line.observedAtUtc
        ? formatUtcDateTime(line.observedAtUtc)
        : formatTimestamp(line.timestamp);

    const segments = useMemo(
      () => buildTextSegments(displayText, lineHighlights),
      [displayText, lineHighlights],
    );

    const hasHighlights = lineHighlights.length > 0;

    return (
      <div
        className={classnames('real-time__line', {
          'real-time__line--partial': line.isFinal === false,
          'real-time__line--search-active': searchHighlight === 'active',
          'real-time__line--no-timestamps': !showTimestamps,
        })}
        data-line-id={line.id}
      >
        <div className="real-time__cell real-time__cell--line">
          {line.pageLineNumber ?? line.lineNumber ?? ''}
        </div>
        {showTimestamps && <div className="real-time__cell real-time__cell--time">{timeLabel}</div>}
        <div className="real-time__cell real-time__cell--text">
          <span className="real-time__text">
            {hasHighlights
              ? segments.map((seg, i) => {
                  if (!seg.highlightId) {
                    return (
                      <React.Fragment key={i}>
                        {onDocRefClick ? renderTextWithDocRefs(seg.text, onDocRefClick) : seg.text}
                      </React.Fragment>
                    );
                  }
                  const tooltip = getHighlightTooltip(seg, lineHighlights);
                  const mark = (
                    <mark
                      className={classnames('real-time__highlight', {
                        'real-time__highlight--noted': seg.hasNote,
                        'real-time__highlight--tagged': seg.hasTags,
                        'real-time__highlight--local-only': seg.isLocalOnly,
                        'real-time__highlight--team': seg.isTeam,
                        'real-time__highlight--navigated':
                          navigatedHighlightId != null && seg.highlightId === navigatedHighlightId,
                      })}
                      data-highlight-id={seg.highlightId}
                      data-local-only={seg.isLocalOnly ? '' : undefined}
                    >
                      {onDocRefClick ? renderTextWithDocRefs(seg.text, onDocRefClick) : seg.text}
                    </mark>
                  );
                  if (seg.isTeam) {
                    return (
                      <span key={seg.highlightId} className="real-time__highlight-wrap">
                        <Tooltip title={tooltip} arrow placement="top">
                          {mark}
                        </Tooltip>
                        {seg.showTeamBadge !== false && (
                          <Tooltip title={getTeamTooltip(seg, lineHighlights)} arrow placement="top">
                            <span className="real-time__team-badge">
                              <PeopleAltIcon style={{ fontSize: 12 }} />
                            </span>
                          </Tooltip>
                        )}
                      </span>
                    );
                  }
                  return (
                    <Tooltip key={seg.highlightId} title={tooltip} arrow placement="top">
                      {mark}
                    </Tooltip>
                  );
                })
              : onDocRefClick
                ? renderTextWithDocRefs(displayText, onDocRefClick)
                : displayText}
            {line.isFinal === false && shouldAnimateLive && (
              <span className="real-time__caret" aria-hidden="true" />
            )}
          </span>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.line === next.line &&
    prev.lineHighlights === next.lineHighlights &&
    prev.shouldAnimateLive === next.shouldAnimateLive &&
    prev.searchHighlight === next.searchHighlight &&
    prev.showTimestamps === next.showTimestamps &&
    prev.onDocRefClick === next.onDocRefClick &&
    prev.navigatedHighlightId === next.navigatedHighlightId,
);

TranscriptLine.displayName = 'TranscriptLine';

export default TranscriptLine;
